// ... existing code ...
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../auth/useAuth';
import {
  Box,
  TextField,
  Button,
  Typography,
  Stack,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../utils/api';
import { withOfflineFallback, readCurrentUser, OfflineNotice, useIsOffline } from '../../offline';
import SetPinDialog from '../../components/SetPinDialog';

const ProfileSettings: React.FC = () => {
  const { user, isLoading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const isOffline = useIsOffline();
  const [showSuccess, setShowSuccess] = useState(false);
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    professionalTitle: '',
    bio: '',
  });

  const { data: profileData, isLoading: profileLoading } = useQuery(
    'userProfile',
    withOfflineFallback(
      async () => {
        const response = await api.get('/users/me');
        return response.data;
      },
      () => readCurrentUser(user?.uid, user?.email ?? undefined),
    ),
  );

  // Hydrate from the query DATA, not onSuccess — onSuccess never fires for a
  // cache-served remount (fresh within staleTime), which left the form empty
  // until a hard refresh. See PracticeSettings for the full rationale.
  useEffect(() => {
    if (!profileData) return; // replica miss — keep the form as-is
    setFormData({
      name: profileData.name || '',
      professionalTitle: profileData.professionalTitle || '',
      bio: profileData.bio || '',
    });
  }, [profileData]);

  // Every role may hold a PIN: it unlocks the lock screen, and an owner's or admin's approves refunds.
  const { data: pinData } = useQuery(
    ['me-approval-pin'],
    async () => (await api.get('/users/me/approval-pin')).data as { hasPin?: boolean },
  );
  // Read from the cache, which SetPinDialog updates on save: null (no chip) until it answers.
  const hasPin = pinData === undefined ? null : pinData?.hasPin ?? null;

  const updateMutation = useMutation(
    (data: typeof formData) => api.put('/users/me', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('userProfile');
        // Refresh the sidebar (App.tsx) name/initials immediately, not after
        // its 5-min staleTime — it reads the same /users/me under this key.
        queryClient.invalidateQueries(['current-user-me']);
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
      },
      onError: (error: any) => {
        setErrorMessage(error?.response?.data?.message || 'Failed to update profile. Please try again.');
      },
    }
  );

  const handleSave = () => {
    updateMutation.mutate(formData);
  };

  if (authLoading || profileLoading) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Box sx={{ maxWidth: 'md' }}>
      <Stack spacing={2}>
        {showSuccess && (
          <Alert severity="success">
            Your profile has been successfully updated.
          </Alert>
        )}

        {errorMessage && (
          <Alert severity="error" onClose={() => setErrorMessage('')}>
            {errorMessage}
          </Alert>
        )}

        {/* Profile writes touch Firebase claims — online-only, never queued. */}
        <OfflineNotice sx={{ mb: 0 }}>
          Profile changes need an internet connection.
        </OfflineNotice>

        <TextField
          label="Email"
          type="email"
          value={user?.email || ''}
          InputProps={{
            readOnly: true,
          }}
          sx={{ backgroundColor: 'grey.50' }}
          fullWidth
        />

        <TextField
          label="Name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          fullWidth
        />

        <TextField
          label="Professional Title"
          value={formData.professionalTitle}
          onChange={(e) => setFormData({ ...formData, professionalTitle: e.target.value })}
          fullWidth
        />

        <TextField
          label="Bio"
          value={formData.bio}
          onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
          multiline
          rows={4}
          fullWidth
        />

        <Box>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isOffline}
            sx={{ mt: 2 }}
          >
            Save Changes
          </Button>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
            <Typography variant="h6" fontWeight="bold">
              Your PIN
            </Typography>
            {hasPin === true && (
              <Chip icon={<CheckCircleIcon />} label="PIN set" color="success" size="small" variant="outlined" />
            )}
            {hasPin === false && <Chip label="Not set yet" size="small" variant="outlined" />}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your 6-digit PIN unlocks StableTrack when your clinic uses the lock screen. If you are an
            owner or admin, it also approves refunds. Setting it asks for your password first.
          </Typography>
          <Button variant="outlined" onClick={() => setPinDialogOpen(true)} disabled={isOffline}>
            {hasPin ? 'Change PIN' : 'Set PIN'}
          </Button>
          <SetPinDialog open={pinDialogOpen} onClose={() => setPinDialogOpen(false)} />
        </Box>
      </Stack>
    </Box>
  );
};

export default ProfileSettings; 